import { useEffect, useRef } from 'react';
import { useModals } from '@/Layouts/MainLayout';
import { syncAutoplayVideo, useResponsiveMedia } from '@/lib/responsiveMedia';

export default function Hero({ openRegister, content = {} }) {
    // If openRegister is not passed, try to get it from context, or fallback to empty function
    const contextModals = useModals();
    const handleRegister = openRegister || contextModals?.openRegister || (() => { });
    const { canAutoplayDecorativeVideo } = useResponsiveMedia();
    const heroVideoRef = useRef(null);

    const heroBg = content.hero_bg || null;
    const smallTitle = content.hero_small_title || 'The Creative Engine';
    const mainTitle = content.hero_main_title || null; // null = show default platform name
    const description = content.hero_description || null;
    const btn1Text = content.hero_btn_1_text || 'Start Creating Free';
    const btn1Url = content.hero_btn_1_url || '';
    const btn2Text = content.hero_btn_2_text || 'View Pricing';
    const btn2Url = content.hero_btn_2_url || '/pricing';
    const isVideoBackground = !!heroBg && /\.(mp4|webm|mov|ogg)(\?.*)?$/i.test(heroBg);
    const showHeroVideo = canAutoplayDecorativeVideo && (isVideoBackground || !heroBg);

    useEffect(() => {
        if (!showHeroVideo || !heroVideoRef.current) {
            return undefined;
        }

        return syncAutoplayVideo(heroVideoRef.current);
    }, [showHeroVideo, heroBg]);

    return (
        <section className="relative flex h-[90vh] min-h-[800px] w-full items-center justify-center overflow-hidden bg-gradient-to-b from-slate-50 via-white to-slate-100 -mt-20 pt-32 transition-colors duration-300 dark:from-background-dark dark:via-background-dark dark:to-[#1a1612]">
            {heroBg ? (
                isVideoBackground ? (
                    showHeroVideo ? (
                        <video
                            key={heroBg}
                            ref={heroVideoRef}
                            className="absolute inset-0 w-full h-full object-cover"
                            autoPlay
                            loop
                            muted
                            playsInline
                            preload="auto"
                        >
                            <source src={heroBg} type="video/mp4" />
                        </video>
                    ) : (
                        <>
                            <div
                                className="absolute inset-0 bg-slate-100 dark:hidden"
                                style={{ backgroundImage: 'radial-gradient(circle at top, rgba(251,146,60,0.18), transparent 48%), linear-gradient(180deg, rgba(255,255,255,0.86), rgba(241,245,249,0.96))' }}
                            />
                            <div
                                className="absolute inset-0 hidden dark:block bg-slate-900/80"
                                style={{ backgroundImage: 'radial-gradient(circle at top, rgba(251,146,60,0.22), transparent 48%), linear-gradient(180deg, rgba(15,23,42,0.2), rgba(15,23,42,0.78))' }}
                            />
                        </>
                    )
                ) : (
                    <img src={heroBg} alt="Hero background" className="absolute inset-0 h-full w-full object-cover" />
                )
            ) : showHeroVideo ? (
                <video
                    ref={heroVideoRef}
                    className="absolute inset-0 w-full h-full object-cover"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                >
                    <source src="/storage/tenants/hsyv5kcc/general/Banner-background.mp4" type="video/mp4" />
                </video>
            ) : (
                <>
                    <div
                        className="absolute inset-0 dark:hidden bg-slate-100"
                        style={{ backgroundImage: 'radial-gradient(circle at top, rgba(251,146,60,0.18), transparent 48%), linear-gradient(180deg, rgba(255,255,255,0.85), rgba(241,245,249,0.95))' }}
                    />
                    <div
                        className="absolute inset-0 hidden dark:block bg-slate-900/90"
                        style={{ backgroundImage: 'radial-gradient(circle at top, rgba(251,146,60,0.2), transparent 48%), linear-gradient(180deg, rgba(15,23,42,0.2), rgba(15,23,42,0.88))' }}
                    />
                </>
            )}
            <div className="absolute inset-0 dark:hidden bg-[radial-gradient(circle_at_top,rgba(251,146,60,0.14),transparent_42%),linear-gradient(180deg,rgba(255,255,255,0.26)_0%,rgba(255,255,255,0.12)_30%,rgba(248,250,252,0.90)_100%)] transition-colors duration-300" />
            <div className="absolute inset-0 hidden dark:block bg-black/40 transition-colors duration-300" />
            <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent dark:from-background-dark dark:via-transparent dark:to-transparent transition-colors duration-300"></div>
            <div className="absolute inset-0 dark:hidden bg-gradient-to-r from-white/55 via-white/10 to-transparent" />

            <div className="relative z-10 text-center px-4 max-w-6xl mx-auto">
                {mainTitle ? (
                    <h2 className="mb-8 text-7xl font-black leading-none tracking-tighter text-slate-950 drop-shadow-[0_10px_24px_rgba(255,255,255,0.42)] transition-colors duration-300 dark:text-white/95 dark:drop-shadow-[0_18px_38px_rgba(0,0,0,0.45)] md:text-[9rem]"
                        dangerouslySetInnerHTML={{ __html: mainTitle }} />
                ) : (
                    <h2 className="mb-8 text-7xl font-black leading-none tracking-tighter text-slate-950 drop-shadow-[0_10px_24px_rgba(255,255,255,0.42)] transition-colors duration-300 dark:text-white/95 dark:drop-shadow-[0_18px_38px_rgba(0,0,0,0.45)] md:text-[11rem]">
                        {import.meta.env.VITE_MASTER_TENANT_NAME}
                    </h2>
                )}
                <p className="mt-6 text-2xl font-semibold uppercase tracking-[0.2em] text-slate-700 transition-colors duration-300 dark:text-white/55 md:text-3xl">
                    {smallTitle}
                </p>
                {description && (
                    <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-700/90 transition-colors duration-300 dark:text-white/65 md:text-xl"
                        dangerouslySetInnerHTML={{ __html: description }} />
                )}
                <div className="mt-12 flex flex-wrap justify-center gap-5">
                    {btn1Url ? (
                        <a
                            href={btn1Url}
                            className="bg-primary hover:bg-primary/90 text-black px-12 py-5 rounded-xl font-bold text-lg transition-all hover:scale-105"
                        >
                            {btn1Text}
                        </a>
                    ) : (
                        <button
                            onClick={handleRegister}
                            className="bg-primary hover:bg-primary/90 text-black px-12 py-5 rounded-xl font-bold text-lg transition-all hover:scale-105"
                        >
                            {btn1Text}
                        </button>
                    )}
                    <a href={btn2Url} className="rounded-xl border-2 border-white/70 bg-white/75 px-12 py-5 text-lg font-semibold text-slate-900 shadow-lg shadow-slate-200/60 transition-all hover:scale-105 hover:bg-white dark:border-primary dark:bg-primary/20 dark:text-white dark:shadow-none dark:hover:bg-primary/30">
                        {btn2Text}
                    </a>
                </div>
            </div>
        </section>
    );
}
